import { useState, useEffect, useCallback } from 'react';
import { Film, Award, Clock, Eye, Star } from 'lucide-react';
import MainLayout from '../../components/MainLayout/MainLayout';
import ContentCard from '../../components/Common/ContentCard';
import Carousel from '../../components/Common/Carousel';
import CarouselItem from '../../components/Common/CarouselItem';
import SecureImage from '../../components/Media/SecureImage';
import mediaService from '../../services/mediaService';
import type { UnifiedContentItem } from '../../types/unifiedContent';
import useSEO from '../../hooks/useSEO';
import { useMediaDuration, formatDuration } from '../../hooks/useMediaDuration';

const CinemaPage = () => {
  useSEO();
  
  const [cinema, setCinema] = useState<UnifiedContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadCinemaContent = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const combinedData = await mediaService.getCinemaCombined({
          page: 0,
          page_size: 100,
        });
        
        setCinema(combinedData || []);
      } catch (err) {
        console.error('Error fetching cinema:', err);
        const errorMessage = err instanceof Error ? err.message : String(err);
        if (errorMessage.includes('Authentication') || errorMessage.includes('credentials') || errorMessage.includes('session')) {
          setError('Unable to load cinema content. Please log in to access this content.');
        } else if (errorMessage.includes('Network') || errorMessage.includes('connection')) {
          setError('Unable to load cinema content. Please check your internet connection and refresh the page.');
        } else {
          setError(errorMessage || 'Unable to load cinema content right now. Please refresh the page or try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    loadCinemaContent();
  }, []);

  const getGroupedCinema = useCallback(() => {
    const sortedItems = [...cinema].sort((a, b) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    if (sortedItems.length < 10) {
      return { mixed: sortedItems };
    }

    const filteredGroups: {[key: string]: UnifiedContentItem[]} = {};
    const usedIds = new Set<string>();
    const minItemsPerSection = 3;

    const featured = sortedItems.filter(item => item.isFeatured).slice(0, 12);
    if (featured.length >= minItemsPerSection) {
      filteredGroups.featured = featured;
      featured.forEach(item => usedIds.add(item.id));
    }

    const remainingItems = sortedItems.filter(item => !usedIds.has(item.id));
    const series = remainingItems.filter(item => item.type === 'SERIES').slice(0, 12);
    if (series.length >= minItemsPerSection) {
      filteredGroups.series = series;
      series.forEach(item => usedIds.add(item.id));
    }

    const trending = sortedItems
      .filter(item => !usedIds.has(item.id) && (item.viewCount || 0) > 0)
      .sort((a, b) => (b.viewCount || 0) - (a.viewCount || 0))
      .slice(0, 12);
    
    if (trending.length >= minItemsPerSection) {
      filteredGroups.trending = trending;
      trending.forEach(item => usedIds.add(item.id));
    }

    const seeMoreItems = sortedItems.filter(item => !usedIds.has(item.id));
    if (seeMoreItems.length > 0) {
      filteredGroups.seeMore = seeMoreItems;
    }

    if (Object.keys(filteredGroups).length === 0) {
      const halfPoint = Math.ceil(sortedItems.length / 2);
      filteredGroups.featured = sortedItems.slice(0, halfPoint);
      if (sortedItems.length > halfPoint) {
        filteredGroups.seeMore = sortedItems.slice(halfPoint);
      }
    }

    return filteredGroups;
  }, [cinema]);

  const convertToContentCard = (item: UnifiedContentItem) => {
    const generateTags = () => {
      const tags = [];
      
      if (item.category) tags.push(item.category);
      
      tags.push('HD', 'Drama');
      
      if (item.type === 'SERIES') tags.push('Series');
      if (item.isFeatured) tags.push('Featured');
      
      return tags.slice(0, 4);
    };

    // Determine the correct type based on whether this is a series or content
    const cardType = item.type === 'SERIES' ? 'series' as const : 'cinema' as const;

    return {
      id: item.id,
      title: item.title,
      thumbnail: item.thumbnailUrl || '',
      type: cardType,
      tags: generateTags(),
      category: item.category,
      // Add series-related props if it's a series
      ...(item.type === 'SERIES' && {
        totalSeasons: item.totalSeasons,
        totalEpisodes: item.totalEpisodes,
        seriesType: item.contentType as 'CINEMA_SERIES' | 'CONTENT_SERIES' | 'PODCAST_SERIES' | 'ANIMATION_SERIES',
      }),
    };
  };

  const heroItem = cinema.find(item => item.isFeatured) || cinema[0];
  const heroDuration = useMediaDuration(heroItem?.mediaUrl, heroItem?.durationSeconds);

  const formatViewCount = (views: number): string => {
    if (views >= 1000000) {
      const millions = views / 1000000;
      return millions % 1 === 0 ? `${millions.toFixed(0)}M` : `${millions.toFixed(1)}M`;
    } else if (views >= 1000) {
      const thousands = views / 1000;
      return thousands % 1 === 0 ? `${thousands.toFixed(0)}K` : `${thousands.toFixed(1)}K`;
    }
    return views.toString();
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading cinema content...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="text-red-500 mb-4">
              <Film className="h-12 w-12 mx-auto mb-2" />
              <p className="text-lg font-semibold">Error Loading Cinema</p>
            </div>
            <p className="text-gray-600 mb-4">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  const groupedCinema = getGroupedCinema();

  return (
    <MainLayout>
      <div className="min-h-screen bg-gray-50">
        <div className="bg-gradient-to-r from-slate-950 via-neutral-900 to-slate-950 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center mb-4">
                  <Film className="h-8 w-8 text-red-400 mr-3" />
                  <span className="text-red-400 font-semibold">StrymHub Cinema Collection</span>
                </div>
                <h1 className="text-5xl md:text-6xl font-bold mb-6">
                  <span className="bg-gradient-to-r from-red-400 via-pink-400 to-orange-400 bg-clip-text text-transparent">
                    Cinema
                  </span>
                </h1>
                <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                  Experience our collection of cinematic masterpieces. From standalone films to complete cinema series - 
                  discover stories that captivate and inspire.
                </p>
                
                <div className="flex flex-wrap gap-8 mb-8">
                  <div className="text-center">
                    <div className="text-2xl font-bold bg-gradient-to-r from-red-400 to-amber-400 bg-clip-text text-transparent">
                      {cinema.length}+
                    </div>
                    <div className="text-sm text-gray-400">Cinema Items</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">
                      {cinema.filter(item => item.type === 'SERIES').length}
                    </div>
                    <div className="text-sm text-gray-400">Series</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent">
                      4K
                    </div>
                    <div className="text-sm text-gray-400">Quality</div>
                  </div>
                </div>

                {/* Animation Elements */}
                <div className="flex items-center space-x-4 mb-8">
                  <Film className="w-6 h-6 text-red-400 animate-pulse" />
                  <div className="flex space-x-2">
                    {[...Array(6)].map((_, i) => (
                      <div
                        key={i}
                        className="w-3 h-3 bg-gradient-to-r from-red-500 to-red-500 rounded-full animate-bounce"
                        style={{ animationDelay: `${i * 0.2}s` }}
                      />
                    ))}
                  </div>
                  <Star className="w-6 h-6 text-yellow-400 animate-pulse" />
                </div>
              </div>
              
              {heroItem && (
                <div className="relative">
                  <div className="relative z-10 rounded-3xl overflow-hidden shadow-2xl">
                    <div className="aspect-[4/5] bg-gradient-to-br from-slate-800 to-slate-900 relative">
                      <SecureImage 
                        src={heroItem.thumbnailUrl?.trim() ?
                             heroItem.thumbnailUrl :
                             `https://picsum.photos/400/600?random=${heroItem.id || 'cinema'}`
                        } 
                        alt={heroItem.title}
                        className="w-full h-full object-cover"
                      />
                      
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20"></div>
                      
                      <div className="absolute top-4 left-4 bg-gradient-to-r from-red-500 to-red-500 text-white px-3 py-1 rounded-full text-sm font-bold flex items-center">
                        <Award className="w-4 h-4 mr-1" />
                        {heroItem.isFeatured ? 'Featured' : 'Cinema'}
                      </div>
                      
                      <div className="absolute bottom-6 left-6 right-6 text-white">
                        {heroItem.category && (
                          <div className="bg-red-500 text-white px-2 py-1 rounded text-xs font-medium mb-2 inline-block">
                            {heroItem.category}
                          </div>
                        )}
                        <h3 className="text-2xl font-bold mb-2">{heroItem.title}</h3>
                        <p className="text-gray-200 text-sm mb-4 line-clamp-2">{heroItem.description}</p>
                        <div className="flex items-center space-x-4 text-sm">
                          <div className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            {heroDuration ? formatDuration(heroDuration) : heroItem.type === 'SERIES' ? `${heroItem.totalEpisodes || 0} episodes` : '0:00'}
                          </div>
                          <div className="flex items-center">
                            <Eye className="w-4 h-4 mr-1" />
                            {formatViewCount(heroItem.viewCount || 0)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="py-8">
          <div className="text-center mb-8 px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Browse Cinema Collection</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Discover our complete cinema library featuring both standalone films and cinema series. 
              Each piece is crafted to deliver an exceptional viewing experience.
            </p>
          </div>

          <div className="space-y-4">
            {Object.entries(groupedCinema).map(([type, items]) => {
              if (!items || items.length === 0) return null;
              
              const typeLabels: {[key: string]: string} = {
                mixed: 'Latest Cinema',
                featured: 'Featured',
                series: 'Cinema Series',
                trending: 'Trending',
                seeMore: 'More Cinema'
              };
              
              return (
                <div key={type} className="mb-6">
                  <Carousel title={typeLabels[type] || type.charAt(0).toUpperCase() + type.slice(1)} id={`cinema-${type}`}>
                    {items.map((item) => {
                      const contentItem = convertToContentCard(item);
                      return (
                        <CarouselItem key={item.id}>
                          <ContentCard {...contentItem} />
                        </CarouselItem>
                      );
                    })}
                  </Carousel>
                </div>
              );
            })}
          </div>

          {cinema.length === 0 && !loading && (
            <div className="text-center py-12">
              <Film className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No cinema content found</h3>
              <p className="text-gray-600">Check back later for new cinema releases.</p>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default CinemaPage;
